// =============================================================================
// SHARED USE TRANSLATION HOOK
// =============================================================================
// React hook for translations - configurable per agent
// =============================================================================

import { useEffect, useCallback } from 'react';
import {
  type LanguageCode,
  SUPPORTED_LANGUAGES,
  isRtlLanguage,
  registerRuntimeLanguages,
  getLanguageMeta,
  hasBundledLocale,
  useFafsaI18nStore,
  useParentAdvocateI18nStore,
  useBenefitsI18nStore,
  useAdminI18nStore,
} from './i18nStore';
import { config } from '../config';

type AgentType = 'fafsa' | 'parent-advocate' | 'benefits' | 'admin';

interface UseTranslationConfig {
  agent: AgentType;
  apiBase?: string;
}

// Map agent types to their stores
const storeMap = {
  'fafsa': useFafsaI18nStore,
  'parent-advocate': useParentAdvocateI18nStore,
  'benefits': useBenefitsI18nStore,
  'admin': useAdminI18nStore,
};

/**
 * Hook for translations
 *
 * Usage:
 *   const { t, language, setLanguage } = useTranslation({ agent: 'fafsa' });
 *   const { t } = useTranslation({ agent: 'parent-advocate' });
 */
export function useTranslation(translationConfig: UseTranslationConfig) {
  const { agent, apiBase = config.apiBase } = translationConfig;

  // Get the correct store for this agent
  const useStore = storeMap[agent];
  const store = useStore();

  const {
    language,
    translations, // Subscribe to translations so component re-renders when they change
    loading,
    error,
    setLanguage: setLang,
    fetchTranslations,
    t,
  } = store;

  // Fetch translations on mount and language change
  useEffect(() => {
    fetchTranslations(agent, language, apiBase);
  }, [agent, language, apiBase, fetchTranslations]);

  // Update document direction for RTL languages
  useEffect(() => {
    // isRtlLanguage, not a lookup in SUPPORTED_LANGUAGES: a district-enabled
    // language is not in the bundle's map, and indexing it there returns
    // undefined — which would render Pashto and Urdu left-to-right.
    document.documentElement.dir = isRtlLanguage(language) ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Change language and trigger refetch
  const setLanguage = useCallback(
    (lang: LanguageCode) => {
      setLang(lang);
      fetchTranslations(agent, lang, apiBase);
    },
    [agent, apiBase, setLang, fetchTranslations]
  );

  // Create a stable t function that updates when translations change
  // This ensures components re-render with new translations
  const translate = useCallback(
    (key: string, fallbackOrVars?: string | Record<string, string | number>, vars?: Record<string, string | number>) => {
      return t(key, fallbackOrVars, vars);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, translations] // Re-create when translations change
  );

  // CAN THIS LANGUAGE BE RENDERED YET?
  //
  // A bundled language: always — the strings are on disk. A language a district
  // enabled: only once its tenant overrides have landed, because there is no
  // bundle to fall back to and t() has no English step. Until then every key
  // renders a marker, which is what a family would actually read.
  //
  // `cachedForLanguage === language` is the honest signal: fetchTranslations
  // sets it only after a response, success or 404, so it means "we asked and we
  // know", not "we are optimistic".
  const ready = hasBundledLocale(agent, language) || store.cachedForLanguage === language;

  return {
    t: translate,
    language,
    setLanguage,
    loading,
    isLoading: loading,
    ready,
    error,
    isRTL: isRtlLanguage(language),
    languages: SUPPORTED_LANGUAGES,
  };
}

export { type LanguageCode, SUPPORTED_LANGUAGES, isRtlLanguage, registerRuntimeLanguages, getLanguageMeta, hasBundledLocale };
